import { useMemo } from 'react';
import { RecoilValueReadOnly, selector, selectorFamily, useRecoilState, useRecoilValue } from 'recoil';
import {
  dashboardsCollectionState,
  dashboardsLoadedState,
  dashboardsState,
  externalDashboardQueriesState,
  externalDashboardsState,
  groupDashboardQueriesState,
  isPublicViewState,
  isSharedState
} from '../../store/dashboardsAtoms';
import { splitter } from '../../helpers/textSplitter';
import { Dashboard } from '../../types/files';

const mappedDashboardsSelector = selector({
  key: "dashboardsMap",
  get: ({ get }) => {
    const dashboards = get(dashboardsState);
    return new Map(dashboards.map(d => [d.id, d]));
  },
});

const mappedOverviewQueriesSelector = selector({
  key: 'mappedOverviewQueriesSelector',
  get: ({ get }) => {
    const groupDashboardQueries = get(groupDashboardQueriesState);
    const externalDashboardQueries = get(externalDashboardQueriesState);

    const allQueries = [...groupDashboardQueries, ...externalDashboardQueries];
    const mappedQueries = new Map<string, any>();

    allQueries.forEach(query => {
      const key = `${query.dashboardId}:${query.title}`;

      if (!mappedQueries.has(key)) {
        const answer: any = JSON.parse(query?.answer || '{}')?.answer ?? '';

        if (query.title === 'Digest')
          mappedQueries.set(key, splitter(answer as string).map(split => split.sent)?.[0]);
        else
          mappedQueries.set(key, answer);
      }
    });

    return mappedQueries;
  },
});

export const selectDashboard = selectorFamily({
  key: "dashboardSelector",
  get: (id: string) => ({ get }) =>
    get(mappedDashboardsSelector).get(id),
});

export const selectDashboardField = (<K extends keyof Dashboard>() =>
  selectorFamily<Dashboard[K] | undefined, { id: string; field: K }>({
    key: "selectDashboardField",
    get: ({ id, field }) => ({ get }) => get(selectDashboard(id))?.[field] ?? undefined
  }))() as <K extends keyof Dashboard>(params: { id: string; field: K;
}) => RecoilValueReadOnly<Dashboard[K] | undefined>;

const useDashboardsContext = () => {
  const [dashboardsLoaded, setDashboardsLoaded] = useRecoilState(dashboardsLoadedState);
  const [dashboards, setDashboards] = useRecoilState(dashboardsState);
  const [externalDashboards, setExternalDashboards] = useRecoilState(externalDashboardsState);
  const [dashboardsCollection, setDashboardsCollection] = useRecoilState(dashboardsCollectionState);
  const [groupDashboardQueries, setDashboardsQueries] = useRecoilState(groupDashboardQueriesState);
  const [externalDashboardQueries, setExternalDashboardQueries] = useRecoilState(externalDashboardQueriesState);
  const [isPublicView, setIsPublicView] = useRecoilState(isPublicViewState);
  const [isShared, setIsShared] = useRecoilState(isSharedState);
  const mappedOverviewQueries = useRecoilValue(mappedOverviewQueriesSelector);

  const context = useMemo(() => ({
    dashboardsLoaded,
    dashboards,
    externalDashboards,
    dashboardsCollection,
    dashboardsQueries: groupDashboardQueries,
    externalDashboardQueries,
    mappedOverviewQueries,
    isPublicView,
    isShared,
    setDashboardsLoaded,
    setDashboards,
    setExternalDashboards,
    setDashboardsCollection,
    setDashboardsQueries,
    setExternalDashboardQueries,
    setIsPublicView,
    setIsShared,
  }), [
    dashboardsLoaded,
    dashboards,
    externalDashboards,
    dashboardsCollection,
    groupDashboardQueries,
    externalDashboardQueries,
    mappedOverviewQueries,
    isPublicView,
    isShared,
    setDashboardsLoaded,
    setDashboards,
    setExternalDashboards,
    setDashboardsCollection,
    setDashboardsQueries,
    setExternalDashboardQueries,
    setIsPublicView,
    setIsShared,
  ]);

  return context;
};

export default useDashboardsContext;